import React from "react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import {
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalSwitch,
    DotLegalTableWithControls,
    ITableHeader,
    DotLegalButton,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { AddEditDeletionPeriodDialog } from "../addEditDeletionPeriodDialog/AddEditDeletionPeriodDialog";
import { useDeletionPeriodsHooks } from "./DeletionPeriods.hooks";
import { useDeletionPeriodsStyles } from "./DeletionPeriods.styles";
import { DeletionPeriodFrequencyType, DeletionPeriodViewModel } from "./DeletionPeriods.types";
import { DeleteDeletionPeriodDialog } from "../deleteDeletionPeriodDialog/DeleteDeletionPeriodDialog";
import { ChangeDeletionPeriodStatusDialog } from "../changeDeletionPeriodStatusDialog/ChangeDeletionPeriodStatusDialog";
import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import CheckIcon from "@mui/icons-material/Check";
import { useMasterDataBreadCrumbProvider } from "../../useMasterDataBreadCrumbProvider";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export function DeletionPeriods() {
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const styles = useDeletionPeriodsStyles();
    const { getBreadCrumbs } = useMasterDataBreadCrumbProvider();

    const {
        isLoading,
        deletionPeriods,
        showAllDeletionsPeriods,
        setShowAllDeletionsPeriods,
        showAddEditDialog,
        setShowAddEditDialog,
        showDeleteDeletionPeriodDialog,
        setShowDeleteDeletionPeriodDialog,
        selectedDeletionPeriod,
        setSelectedDeletionPeriod,
        refetchDeletionPeriods,
        showChangeStatusDialog,
        setShowChangeStausDialog,
    } = useDeletionPeriodsHooks();

    const getMenuItems = (dp: DeletionPeriodViewModel) => {
        let menuItems = [];
        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setSelectedDeletionPeriod({
                        name: dp.name,
                        description: dp.description,
                        id: dp.id,
                        deletionPeriodLength: dp.deletionPeriodLength,
                        deletionPeriodType: Number(dp.deletionPeriodType!.toString()),
                        active: dp.active,
                        isCustom: dp.isCustom,
                    });
                    setShowAddEditDialog(true);
                },
            });

            menuItems.push({
                menuItem: dp.active ? translateString("deactivate") : translateString("activate"),
                onClick: () => {
                    setSelectedDeletionPeriod({
                        name: dp.name,
                        description: dp.description,
                        id: dp.id,
                        deletionPeriodLength: dp.deletionPeriodLength,
                        deletionPeriodType: Number(dp.deletionPeriodType!.toString()),
                        active: dp.active,
                        isCustom: dp.isCustom,
                    });
                    setShowChangeStausDialog(true);
                },
            });
        }

        if (permissions.canManageMasterData && dp.isCustom) {
            menuItems.push({
                menuItem: translateString("removeDeletionPeriod"),
                onClick: () => {
                    setSelectedDeletionPeriod({
                        name: dp.name,
                        description: dp.description,
                        id: dp.id,
                        deletionPeriodLength: dp.deletionPeriodLength,
                        deletionPeriodType: Number(dp.deletionPeriodType!.toString()),
                        active: dp.active,
                        isCustom: dp.isCustom,
                    });
                    setShowDeleteDeletionPeriodDialog(true);
                },
            });
        }
        return menuItems;
    };

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs(translateString("deletionPeriods"))}
                leftComponent={
                    <DotLegalSwitch
                        onChange={setShowAllDeletionsPeriods}
                        checked={showAllDeletionsPeriods}
                        leftLabel={translateString("active")}
                        rightLabel={translateString("all")}
                    />
                }
                rightComponent={
                    permissions.canManageMasterData ? (
                        <DotLegalButton
                            buttonType="primary"
                            size="medium"
                            onClick={() => {
                                setShowAddEditDialog(true);
                                setSelectedDeletionPeriod(undefined);
                            }}
                        >
                            {translateString("createDeletionPeriod")}
                        </DotLegalButton>
                    ) : undefined
                }
            />
            <TablePagePaper>
                <DotLegalTableWithControls
                    internalScroll
                    headers={getHeaders(translateString)}
                    isLoading={isLoading}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    getUserSpecificPageLength={() => 30}
                    hideRowsPerPage
                    clickableRows={false}
                    hasOverflowMenu
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    emptyText={translateString("noData")}
                    data={deletionPeriods}
                    renderRow={(row, i) => (
                        <TableRow key={i} inactive={!row.active} inactiveTooltip={translateString("deactivated")}>
                            <TableCell sx={styles.bold}>{row.name}</TableCell>
                            <TableCell>{row.description}</TableCell>
                            <TableCell>{translateString(DeletionPeriodFrequencyType[row.deletionPeriodType as number].toLowerCase())}</TableCell>
                            <TableCell align="right">{row.deletionPeriodLength?.toString() ?? ""}</TableCell>
                            <TableCell>
                                <Box sx={styles.iconContainer}>{row.active ? <CheckIcon sx={styles.checkIcon} /> : ""}</Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={styles.iconContainer}>{!row.isCustom ? <CheckIcon sx={styles.checkIcon} /> : ""}</Box>
                            </TableCell>
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>

            {showDeleteDeletionPeriodDialog && (
                <DeleteDeletionPeriodDialog
                    selectedDeletionPeriod={selectedDeletionPeriod!}
                    onSaveCorrect={refetchDeletionPeriods}
                    onCloseDialog={() => setShowDeleteDeletionPeriodDialog(false)}
                />
            )}

            {showChangeStatusDialog && (
                <ChangeDeletionPeriodStatusDialog
                    selectedDeletionPeriod={selectedDeletionPeriod!}
                    refetch={refetchDeletionPeriods}
                    onCloseDialog={() => setShowChangeStausDialog(false)}
                />
            )}
            {showAddEditDialog && (
                <AddEditDeletionPeriodDialog
                    deletionPeriod={selectedDeletionPeriod}
                    isAdd={selectedDeletionPeriod === undefined}
                    showGlobal
                    open={showAddEditDialog}
                    onDialogClose={() => setShowAddEditDialog(false)}
                    onOkClick={() => setShowAddEditDialog(false)}
                    refetch={refetchDeletionPeriods}
                />
            )}
        </Box>
    );
}

function getHeaders(translateString: any) {
    let headers = Array<ITableHeader<DeletionPeriodViewModel>>();
    headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
    headers.push({ property: "description", text: translateString("description"), align: "left", showOnMobile: true });
    headers.push({ property: "deletionPeriodType", text: translateString("deletionPeriodType"), align: "left", showOnMobile: true });
    headers.push({ property: "deletionPeriodLength", text: translateString("deletionPeriodLength"), align: "left", showOnMobile: true });
    headers.push({ property: "active", text: translateString("active"), align: "left", showOnMobile: true });
    headers.push({ property: "isCustom", text: translateString("standardMasterData"), align: "left", showOnMobile: true });

    return headers;
}
